import { addDays, type Quote, type QuoteDraft } from "../quotes/types";

export type RecurringFreq = "weekly" | "biweekly" | "monthly" | "quarterly" | "semiannual" | "yearly";

export const RECURRING_FREQS: RecurringFreq[] = [
  "weekly",
  "biweekly",
  "monthly",
  "quarterly",
  "semiannual",
  "yearly",
];

export function asRecFreq(v: string | null | undefined): RecurringFreq {
  return RECURRING_FREQS.includes(v as RecurringFreq) ? (v as RecurringFreq) : "monthly";
}
export type RecurringStatus = "draft" | "running" | "paused" | "ended";

export type Recurring = {
  id: number;
  company_id: number;
  client_id: number;
  client_name?: string | null;
  document_id: number;
  number?: string | null;
  frequency: RecurringFreq;
  starts_on: string;
  next_run_on: string;
  cycles_left: number | null;
  status: RecurringStatus;
  waiting?: boolean;
  payment_term_id: number | null;
  payment_term_label?: string;
  due_days?: number | null;
  due_mode?: string;
  total_ht_cents?: number;
  total_ttc_cents: number;
  document?: Quote;
};

export type RecurringDraft = {
  id?: number;
  number?: string | null;
  client_id: number;
  frequency: RecurringFreq;
  starts_on: string;
  cycles_left: number | null;
  status: RecurringStatus;
  waiting?: boolean;
  payment_term_id: number | null;
  payment_term_label: string;
  due_days: number;
  next_run_on?: string;
  quote: QuoteDraft;
};

export function recDueDays(n: number | null | undefined): number {
  const v = Math.round(Number(n));
  if (!Number.isFinite(v) || v < 0) return 0;
  if (v > 31) return 31;
  return v;
}

export function recDaysBetween(from: string, to: string): number {
  const a = new Date(`${from}T12:00:00`);
  const b = new Date(`${to}T12:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

export function recIssueOn(row: { starts_on?: string | null; next_run_on?: string | null; status?: string }): string | null {
  if (row.status === "ended") return row.starts_on || null;
  return row.next_run_on || row.starts_on || null;
}

export function recDueOn(row: {
  starts_on?: string | null;
  next_run_on?: string | null;
  due_days?: number | null;
  status?: string;
}): string | null {
  const issue = recIssueOn(row);
  if (!issue) return null;
  return addDays(issue, recDueDays(row.due_days));
}

export function recStatusLabel(status: RecurringStatus | string, waiting = false): string {
  if (status === "draft") return "Brouillon";
  if (waiting) return "En attente";
  if (status === "running") return "Active";
  if (status === "paused") return "En pause";
  if (status === "ended") return "Terminée";
  return "Brouillon";
}

export function recStatusTone(status: RecurringStatus | string, waiting = false): string {
  if (status === "draft") return "draft";
  if (waiting) return "rec-wait";
  if (status === "running") return "rec-on";
  if (status === "paused") return "rec-pause";
  if (status === "ended") return "rec-end";
  return "draft";
}

export function recFreqLabel(freq: RecurringFreq | string): string {
  if (freq === "weekly") return "Toutes les semaines";
  if (freq === "biweekly") return "Toutes les 2 semaines";
  if (freq === "quarterly") return "Trimestriel";
  if (freq === "semiannual") return "Semestriel";
  if (freq === "yearly") return "Annuel";
  return "Mensuel";
}

export function recCyclesLabel(n: number | null | undefined): string {
  if (n === null || n === undefined) return "Sans fin";
  return String(n);
}
